/**
 * Every product/collection/page URL is meant to feel like it was opened
 * from the map — a translucent panel over the map, never a flat solid
 * page. That's normally true because navigating there goes through
 * couor-map-menu.js's panel. But a cold load (refresh, direct link, a
 * search result) skips all of that and just renders the bare section —
 * solid background, no map behind it.
 *
 * This script detects that case and retrofits it: fetch the homepage,
 * swap it in as the page background, then move the content that was
 * already on screen into the map panel and open it. Runs on every page
 * (tiny/no-op unless needed) so refreshing a product page never again
 * lands on a flat black screen instead of the translucent map view.
 */
// The third-party countdown app injects a fixed "coming soon" bar at the
// bottom of product and collection pages at runtime -- it exists in no
// theme file, so the theme removes it on sight instead. The sweep is
// deliberately narrow: a fixed/sticky element, sitting directly on <body>
// (all of the theme's own chrome lives inside the map section or is
// explicitly excluded), whose text says "coming soon". A MutationObserver
// re-runs it, so a late injection does not survive either.
(() => {
  function sweepComingSoonBar() {
    document.querySelectorAll('body > *').forEach((el) => {
      if (
        el.matches(
          'script, style, link, main, couor-map, cart-drawer, .couor-map-section, .couor-cart-icon, .couor-back-to-map, .couor-loupe, ul, a'
        )
      )
        return;
      const cs = getComputedStyle(el);
      if (cs.position !== 'fixed' && cs.position !== 'sticky') return;
      if ((el.textContent || '').toLowerCase().includes('coming soon')) el.remove();
    });
  }
  const arm = () => {
    sweepComingSoonBar();
    new MutationObserver(sweepComingSoonBar).observe(document.body, { childList: true });
    setTimeout(sweepComingSoonBar, 2500);
    setTimeout(sweepComingSoonBar, 6000);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arm, { once: true });
  else arm();
})();

(() => {
  const mainEl = document.getElementById('MainContent');
  if (!mainEl) return;

  const template = mainEl.dataset.template;
  const bootstrappable = { product: 'product', collection: 'grid', page: 'page', cart: 'page' };
  if (!(template in bootstrappable)) return;
  // Native mode: theme.liquid renders the map section directly on these
  // templates now, so there is nothing to fetch and nothing to swap -- the
  // page's own content just moves into the panel, listeners intact. The
  // fetch-and-swap machinery below survives only as the fallback for any
  // template that still arrives without a map.
  const nativeMap = !!document.querySelector('couor-map');

  function captureCurrentContent() {
    if (template === 'product') {
      const el = document.getElementById('couor-product');
      const titleEl = el ? el.querySelector('.product-info__title-top') : null;
      return { el, label: titleEl ? titleEl.textContent.trim() : document.title };
    }
    if (template === 'collection') {
      const el = document.querySelector('ul#product-grid');
      return { el, label: document.title };
    }
    return { el: mainEl, label: document.title };
  }

  const { el: contentEl, label } = captureCurrentContent();
  if (!contentEl) return;
  const savedContent = nativeMap ? contentEl : contentEl.cloneNode(true);

  // cloneNode copies attributes but never event listeners. If the cold
  // page's own inline script already ran and marked itself "bound" (e.g.
  // couor-product.liquid's dataset.couorProductBound guard), that flag
  // rides along on the clone and makes the *revived* copy of that same
  // script think it's already wired up — so it skips re-attaching
  // listeners entirely, and the clone (with none of the original's
  // listeners) ends up inert. Strip any such flags before reuse.
  savedContent.querySelectorAll('[data-couor-product-bound]').forEach((el) => {
    delete el.dataset.couorProductBound;
  });
  if (savedContent.dataset) delete savedContent.dataset.couorProductBound;
  const mode = bootstrappable[template];

  // The section's own stylesheet (e.g. couor-product.css) is a *sibling*
  // of the content div we just cloned, not a descendant — it lives in the
  // <main> we're about to blow away with the body swap below. Without
  // this, the re-inserted content has no styling at all.
  const originalStylesheets = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map((link) =>
    link.cloneNode(true)
  );

  // Every external script that has ALREADY EXECUTED on this page, captured
  // before the body swap destroys their elements. Element-presence checks
  // can't cover this case: the swap removes the original <script> nodes,
  // but their JS (Dawn bundles declaring top-level classes) has still run —
  // re-running one throws "Identifier has already been declared" and aborts
  // the rest of that file, which is exactly the CartNotification /
  // PredictiveSearch console noise this set prevents.
  const executedSrcs = new Set(
    Array.from(document.querySelectorAll('script[src]')).map((s) => s.getAttribute('src'))
  );

  function reviveScripts(root) {
    root.querySelectorAll('script').forEach((oldScript) => {
      const src = oldScript.getAttribute('src');
      // Already ran before the swap, or is present elsewhere in the live
      // document (excluding oldScript itself — it's already in the document,
      // inert, by the time we walk it).
      const alreadyLoaded =
        src &&
        (executedSrcs.has(src) ||
          Array.from(document.querySelectorAll(`script[src="${src}"]`)).some((s) => s !== oldScript));
      if (alreadyLoaded) {
        oldScript.remove();
        return;
      }
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attr) => newScript.setAttribute(attr.name, attr.value));
      if (src) newScript.setAttribute('data-couor-revived', '');
      newScript.async = false; // preserve source order once each finishes loading
      newScript.textContent = oldScript.textContent;
      oldScript.replaceWith(newScript);
    });
  }

  // Which collections this content belongs to — read before the body swap.
  const collectionHandles = (() => {
    if (template === 'product') {
      return (contentEl.dataset.collections || '').split(/\s+/).filter(Boolean);
    }
    if (template === 'collection') {
      const match = location.pathname.match(/\/collections\/([^/?#]+)/);
      return match ? [decodeURIComponent(match[1])] : [];
    }
    return [];
  })();

  // Where on the map this content lives. Prefer the region actually
  // travelled to this session (exact, and survives drilling down from a
  // collection into a product); fall back to matching the content's
  // collection against the regions' linked collections, which is what
  // makes a cold link from an ad still open over the right territory.
  function resolveRegionAnchor() {
    // The URL is the truth: a refresh of /collections/new-releases belongs
    // to Bermuda no matter where this session last travelled. The stored
    // session region used to be consulted FIRST, which titled and flew a
    // refreshed collection to whatever region was visited last -- panel
    // saying "The Archive" over the New Releases grid.
    // Regions declare their collection by handle, so a collection URL
    // matches directly -- URL matching against menu items broke whenever
    // the Shopify menu pointed a region somewhere else (Bermuda's menu
    // entry opens a page, not the collection).
    for (const handle of collectionHandles) {
      const regionEl = document.querySelector(`couor-map-region[data-collection="${CSS.escape(handle)}"]`);
      if (regionEl && regionEl.dataset.anchor) return regionEl.dataset.anchor;
    }
    const menuItems = Array.from(document.querySelectorAll('[data-map-menu-item][data-anchor]'));
    for (const handle of collectionHandles) {
      const match = menuItems.find((i) => (i.dataset.contentUrl || '').includes(`/collections/${handle}`));
      if (match && match.dataset.anchor) return match.dataset.anchor;
    }
    // Only when the URL itself names no region (a page, a product outside
    // every region collection) does the session's last travel decide.
    try {
      const stored = sessionStorage.getItem('couor:region');
      if (stored) return stored;
    } catch (err) {
      /* storage unavailable */
    }
    return null;
  }

  // The map renders asynchronously (it fetches its geometry), so wait for
  // it to finish before asking it to move, or the request lands on nothing
  // and the view stays zoomed out to the whole world.
  function restoreRegion(attempt) {
    const anchor = resolveRegionAnchor();
    if (!anchor) return;
    const mapEl = document.querySelector('couor-map');
    if (!mapEl || !mapEl.classList.contains('is-loaded')) {
      if (attempt < 60) setTimeout(() => restoreRegion(attempt + 1), 100);
      return;
    }
    document.dispatchEvent(new CustomEvent('couor:fly-to', { detail: { anchor, immediate: true } }));
  }

  // The menu's travelled state, set the moment the region is known.
  // couor-map.js does the same in setActiveLocation when the flight lands,
  // but on content pages the artwork is deferred -- waiting for it left the
  // menu sitting centered in its default stack for the first second after a
  // refresh, then visibly jumping to its anchored position.
  function markMenuActive(name) {
    if (!name) return;
    const menu = document.querySelector('.couor-map__menu');
    if (menu) menu.classList.add('is-active');
    const label = document.querySelector('[data-active-location]');
    if (label) {
      label.textContent = name;
      label.hidden = false;
    }
  }

  function attachToPanel(attempt) {
    const panel = document.querySelector('[data-map-panel]');
    const body = panel ? panel.querySelector('[data-map-panel-body]') : null;
    const title = panel ? panel.querySelector('[data-panel-title]') : null;

    if (!panel || !body) {
      // couor-map-menu.js's script is still loading/executing — retry briefly.
      if (attempt < 20) setTimeout(() => attachToPanel(attempt + 1), 50);
      return;
    }

    body.innerHTML = '';
    body.appendChild(savedContent);
    // Dawn hides scroll-reveal elements at opacity 0.01 until animations.js
    // observes them scrolling into view; content dropped straight into the
    // panel is never observed, so strip the class or it stays invisible.
    body.querySelectorAll('.scroll-trigger').forEach((el) => {
      el.classList.remove('scroll-trigger', 'scroll-trigger--offscreen');
    });
    if (!nativeMap) reviveScripts(body);
    // The content main ships hidden in native mode so the flat page never
    // flashes before the panel; whatever moved out of it must not carry
    // that state along.
    savedContent.removeAttribute('hidden');
    if (title) {
      // For a collection, head the panel with the territory's name off the
      // map (same as arriving via the menu) rather than the document title,
      // which reads like a browser tab ("Products – By Couor"). Resolvable
      // only now — the region elements arrive with the homepage swap.
      let heading = label || '';
      if (mode === 'grid') {
        const anchor = resolveRegionAnchor();
        const regionEl = anchor && document.querySelector(`couor-map-region[data-anchor="${anchor}"]`);
        if (regionEl && regionEl.dataset.name) {
          heading = regionEl.dataset.name;
          markMenuActive(heading);
        } else heading = heading.replace(/\s*[–-]\s*[^–-]*$/, ''); // drop the "– Shop Name" tail
      } else if (mode === 'page') {
        // A refresh must title the page exactly as the menu path does:
        // find the menu item this page belongs to. A region item (like
        // Bermuda pointing at a coming-soon page) titles it with the
        // REGION's name off the map; a plain item (About) titles it with
        // the menu label. Only a page reached outside the menu falls back
        // to document.title, minus Shopify's "– Shop Name" tail.
        const pageItem = Array.from(document.querySelectorAll('[data-map-menu-item]')).find((i) => {
          const urls = [i.dataset.contentUrl, i.dataset.href];
          return urls.some((u) => u && u.split('#')[0] === location.pathname);
        });
        if (pageItem) {
          const regionEl =
            pageItem.dataset.anchor &&
            document.querySelector(`couor-map-region[data-anchor="${pageItem.dataset.anchor}"]`);
          heading = (regionEl && regionEl.dataset.name) || pageItem.textContent.trim();
          if (regionEl && regionEl.dataset.name) markMenuActive(heading);
          // A region's page also gets the map back over its territory —
          // but the map loads its artwork asynchronously, so wait for it
          // (same dance as restoreRegion) or the event lands on nothing.
          if (pageItem.dataset.anchor) {
            const anchor = pageItem.dataset.anchor;
            (function flyWhenReady(tries) {
              const mapEl = document.querySelector('couor-map');
              if (!mapEl || !mapEl.classList.contains('is-loaded')) {
                if (tries < 60) setTimeout(() => flyWhenReady(tries + 1), 100);
                return;
              }
              document.dispatchEvent(new CustomEvent('couor:fly-to', { detail: { anchor, immediate: true } }));
            })(0);
          }
        } else {
          heading = heading.replace(/\s*[–-]\s*[^–-]*$/, '');
        }
      }
      title.textContent = heading;
    }
    panel.classList.remove('couor-map-panel--grid', 'couor-map-panel--product', 'couor-map-panel--page');
    if (mode) panel.classList.add(`couor-map-panel--${mode}`);
    // Mirror couor-map-menu.js setPanelMode: the compass's collection-
    // navigator state keys off this body flag, and cold direct-URL loads
    // come through HERE, never through the menu's setPanelMode.
    document.body.classList.toggle('couor-grid-open', mode === 'grid');
    document.body.classList.toggle('couor-product-open', mode === 'product');
    // Cold-load grids, mirroring the menu's prepGridImages: pin the 720
    // rendition, drop srcset, mark CORS BEFORE the (re)fetch so the
    // keyer reads the displayed pixels without a second download, and
    // eager the first screenful (the Safari lazy flake). The keying
    // hold keeps them invisible until keyed, so the swap never flashes.
    if (mode === 'grid') {
      panel.querySelectorAll('#product-grid img').forEach((im, i) => {
        const src = im.getAttribute('src');
        if (src && !src.startsWith('blob:')) {
          im.setAttribute('src', src.replace(/([?&]width=)\d+/, '$1720'));
        }
        im.removeAttribute('srcset');
        im.removeAttribute('sizes');
        im.setAttribute('crossorigin', 'anonymous');
        if (i < 4) {
          im.loading = 'eager';
          if (i < 2) im.setAttribute('fetchpriority', 'high');
        }
      });
    }
    panel.hidden = false;
    // Force a style flush, then add the class synchronously — see the same
    // note in couor-map-menu.js. requestAnimationFrame never fires while
    // the tab is hidden, which would leave the panel stuck at opacity 0.
    void panel.offsetHeight;
    panel.classList.add('is-open');
    document.body.classList.add('couor-nav-open');
    history.replaceState({ couorPanel: true }, '', location.pathname + location.search);

    // Put the map back over the territory this content belongs to. Pages
    // (About/Contact) aren't tied to a region, so they keep the default view.
    if (template !== 'page') restoreRegion(0);
  }

  function begin() {
    fetch('/', { priority: 'low' })
      .then((res) => {
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        return res.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        document.body.innerHTML = doc.body.innerHTML;

        const presentHrefs = new Set(Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map((l) => l.href));
        originalStylesheets.forEach((link) => {
          if (link.href && !presentHrefs.has(link.href)) {
            document.head.appendChild(link);
            presentHrefs.add(link.href);
          }
        });

        reviveScripts(document.body);
        attachToPanel(0);
      })
      .catch((err) => {
        console.error('[couor-map-bootstrap] failed to load map background', err);
      });
  }

  // Ad traffic lands directly on product pages, mostly on phones. The map
  // (SVG + creature art, ~1.2MB) is the *backdrop* there, not the content —
  // starting it immediately made it race the product photos for cellular
  // bandwidth and pushed LCP way out. Wait for the page's own load event
  // (plus an idle slot), then bring the map in behind the finished page.
  const idle = (fn) => (window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 2000 }) : setTimeout(fn, 200));
  if (nativeMap) {
    attachToPanel(0);
  } else if (document.readyState === 'complete') {
    idle(begin);
  } else {
    window.addEventListener('load', () => idle(begin), { once: true });
  }
})();
